/**
 * TagGroupSelector - 标签选择器组件
 * 用于导入向导的 Step 1：选择导入范围
 *
 * 按照 3.0-phase-2-import-plan.md Section 5.3 设计：
 * - 标签组列表（checkbox，勾选=包含所有子标签）
 * - 未分组标签（checkbox）
 * - 未分类帖子（checkbox）- 没有任何标签的帖子
 * - 显示帖子/博主计数
 */

import React, { useMemo } from 'react';
import { Checkbox, Empty } from 'antd';
import { FolderIcon, HashIcon } from 'lucide-react';
import {
  CaretDown as CaretDownIcon,
  CaretRight as CaretRightIcon,
  StackOverflowLogo as StackOverflowLogoIcon,
} from '@phosphor-icons/react';
import type {
  LocalTagGroupForImport,
  LocalTagForImport,
} from '../types';

export interface TagGroupSelectorProps {
  /** 本地标签组列表 */
  tagGroups: LocalTagGroupForImport[];
  /** 未分组的标签列表 */
  ungroupedTags: LocalTagForImport[];
  /** 未分类帖子数量 */
  uncategorizedPostCount?: number;
  /** 已选中的标签组 ID */
  selectedTagGroupIds: string[];
  /** 已选中的未分组标签 ID */
  selectedTagIds: string[];
  /** 是否选中未分类帖子 */
  includeUncategorized?: boolean;
  /** 选择变更回调 */
  onSelectionChange: (selection: {
    selectedTagGroupIds: string[];
    selectedTagIds: string[];
    includeUncategorized?: boolean;
  }) => void;
  /** 加载中状态 */
  loading?: boolean;
}

export const TagGroupSelector: React.FC<TagGroupSelectorProps> = ({
  tagGroups,
  ungroupedTags,
  uncategorizedPostCount = 0,
  selectedTagGroupIds,
  selectedTagIds,
  includeUncategorized = false,
  onSelectionChange,
  loading = false,
}) => {
  // 展开状态（本地管理）
  const [expandedGroups, setExpandedGroups] = React.useState<Record<string, boolean>>(() => {
    // 默认全部展开
    const expanded: Record<string, boolean> = {};
    tagGroups.forEach((g) => {
      expanded[g.id] = true;
    });
    return expanded;
  });

  // 切换标签组展开/收起
  const toggleExpand = (groupId: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  // 选中/取消选中标签组
  const handleToggleTagGroup = (groupId: string, checked: boolean) => {
    let newSelectedGroupIds: string[];
    if (checked) {
      newSelectedGroupIds = [...selectedTagGroupIds, groupId];
    } else {
      newSelectedGroupIds = selectedTagGroupIds.filter((id) => id !== groupId);
    }
    onSelectionChange({
      selectedTagGroupIds: newSelectedGroupIds,
      selectedTagIds,
      includeUncategorized,
    });
  };

  // 选中/取消选中未分组标签
  const handleToggleTag = (tagId: string, checked: boolean) => {
    let newSelectedTagIds: string[];
    if (checked) {
      newSelectedTagIds = [...selectedTagIds, tagId];
    } else {
      newSelectedTagIds = selectedTagIds.filter((id) => id !== tagId);
    }
    onSelectionChange({
      selectedTagGroupIds,
      selectedTagIds: newSelectedTagIds,
      includeUncategorized,
    });
  };

  // 选中/取消选中未分类帖子
  const handleToggleUncategorized = (checked: boolean) => {
    onSelectionChange({
      selectedTagGroupIds,
      selectedTagIds,
      includeUncategorized: checked,
    });
  };

  // 全选/取消全选
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      onSelectionChange({
        selectedTagGroupIds: tagGroups.map((g) => g.id),
        selectedTagIds: ungroupedTags.map((t) => t.id),
        includeUncategorized: uncategorizedPostCount > 0,
      });
    } else {
      onSelectionChange({
        selectedTagGroupIds: [],
        selectedTagIds: [],
        includeUncategorized: false,
      });
    }
  };

  // 计算是否全选
  const isAllSelected = useMemo(() => {
    const totalGroups = tagGroups.length;
    const totalTags = ungroupedTags.length;
    const hasUncategorized = uncategorizedPostCount > 0;

    if (totalGroups === 0 && totalTags === 0 && !hasUncategorized) return false;

    const groupsSelected = selectedTagGroupIds.length === totalGroups;
    const tagsSelected = selectedTagIds.length === totalTags;
    const uncategorizedSelected = !hasUncategorized || includeUncategorized;

    return groupsSelected && tagsSelected && uncategorizedSelected;
  }, [tagGroups, ungroupedTags, uncategorizedPostCount, selectedTagGroupIds, selectedTagIds, includeUncategorized]);

  // 计算是否部分选中
  const isIndeterminate = useMemo(() => {
    const hasSelection = selectedTagGroupIds.length > 0 || selectedTagIds.length > 0 || includeUncategorized;
    return hasSelection && !isAllSelected;
  }, [selectedTagGroupIds, selectedTagIds, includeUncategorized, isAllSelected]);

  // 空状态
  if (!loading && tagGroups.length === 0 && ungroupedTags.length === 0 && uncategorizedPostCount === 0) {
    return (
      <Empty
        description="本地没有任何标签或帖子数据"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* 全选选项 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '2px 4px',
          // backgroundColor: 'var(--ant-color-fill-tertiary)',
          borderRadius: 6,
          marginBottom: 8,
        }}
      >
        <Checkbox
          checked={isAllSelected}
          indeterminate={isIndeterminate}
          onChange={(e) => handleSelectAll(e.target.checked)}
        >
          <span style={{ fontWeight: 600 }}>全选</span>
        </Checkbox>
      </div>

      {/* 未分类帖子选项 */}
      {uncategorizedPostCount > 0 && (
        <>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '8px 12px',
              borderRadius: 6,
              backgroundColor: includeUncategorized
                ? 'var(--ant-color-primary-bg)'
                : 'var(--ant-color-fill-quaternary)',
              transition: 'background-color 0.2s',
            }}
          >
            {/* 占位对齐 */}
            <span style={{ width: 22 }} />

            {/* 复选框 */}
            <Checkbox
              checked={includeUncategorized}
              onChange={(e) => handleToggleUncategorized(e.target.checked)}
            />

            {/* 图标和名称 */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginLeft: 8,
                flex: 1,
              }}
            >
              <StackOverflowLogoIcon size={16} weight="bold" style={{ color: 'var(--ant-color-warning)' }} />
              <span style={{ color: 'var(--ant-color-text)', fontWeight: 500 }}>未分类帖子</span>
            </div>

            {/* 统计信息 */}
            <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)' }}>
              {uncategorizedPostCount} 篇帖子
            </span>
          </div>
        </>
      )}

          {/* 分割线 */}
          {/* {(tagGroups.length > 0 || ungroupedTags.length > 0) && (
            <div
              style={{
                margin: '8px 0',
                borderTop: '1px solid var(--ant-color-split)',
              }}
            />
          )} */}

      {/* 标签组列表 */}
      {tagGroups.map((group) => {
        const isExpanded = expandedGroups[group.id];
        const isSelected = selectedTagGroupIds.includes(group.id);

        return (
          <div key={group.id} style={{ marginBottom: 4 }}>
            {/* 标签组项 */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px 12px',
                borderRadius: 6,
                backgroundColor: isSelected
                  ? 'var(--ant-color-primary-bg)'
                  : 'var(--ant-color-fill-quaternary)',
                transition: 'background-color 0.2s',
              }}
            >
              {/* 展开/收起按钮 */}
              <span
                onClick={() => toggleExpand(group.id)}
                style={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  marginRight: 8,
                  color: 'var(--ant-color-text-tertiary)',
                }}
              >
                {group.tags.length > 0 ? (
                  isExpanded ? <CaretDownIcon size={14} /> : <CaretRightIcon size={14} />
                ) : (
                  <span style={{ width: 14 }} />
                )}
              </span>

              {/* 复选框 */}
              <Checkbox
                checked={isSelected}
                onChange={(e) => handleToggleTagGroup(group.id, e.target.checked)}
              />

              {/* 标签组图标和名称 */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginLeft: 8,
                  flex: 1,
                }}
              >
                <FolderIcon size={16} style={{ color: 'var(--ant-color-primary)' }} />
                <span style={{ fontWeight: 600, color: 'var(--ant-color-text)' }}>
                  {group.name}
                </span>
              </div>

              {/* 统计信息 */}
              <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)' }}>
                {group.tags.length} 个标签 · {group.totalPostCount} 篇帖子
              </span>
            </div>

            {/* 子标签列表（展开时显示，仅供参考） */}
            {isExpanded && group.tags.length > 0 && (
              <div style={{ marginLeft: 42, marginTop: 4 }}>
                {group.tags.map((tag) => (
                  <div
                    key={tag.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '4px 12px',
                      borderRadius: 4,
                      backgroundColor: isSelected
                        ? 'var(--ant-color-primary-bg-hover)'
                        : 'transparent',
                    }}
                  >
                    <HashIcon
                      size={14}
                      style={{
                        color: 'var(--ant-color-text-quaternary)',
                        marginRight: 8,
                      }}
                    />
                    <span style={{ color: 'var(--ant-color-text-secondary)', flex: 1 }}>
                      {tag.name}
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--ant-color-text-quaternary)' }}>
                      {tag.postCount} 篇
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}


      {/* 未分组标签列表 */}
      {ungroupedTags.map((tag) => {
        const isSelected = selectedTagIds.includes(tag.id);

        return (
          <div
            key={tag.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '8px 12px',
              borderRadius: 6,
              backgroundColor: isSelected
                ? 'var(--ant-color-primary-bg)'
                : 'var(--ant-color-fill-quaternary)',
              transition: 'background-color 0.2s',
            }}
          >
            {/* 占位对齐 */}
            <span style={{ width: 22 }} />

            {/* 复选框 */}
            <Checkbox
              checked={isSelected}
              onChange={(e) => handleToggleTag(tag.id, e.target.checked)}
            />

            {/* 标签图标和名称 */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginLeft: 8,
                flex: 1,
              }}
            >
              <HashIcon size={16} style={{ color: 'var(--ant-color-text-tertiary)' }} />
              <span style={{ color: 'var(--ant-color-text)' }}>{tag.name}</span>
            </div>

            {/* 统计信息 */}
            <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)' }}>
              {tag.postCount} 篇帖子
            </span>
          </div>
        );
      })}


    </div>
  );
};

export default TagGroupSelector;
